<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type ProjectDto } from "@/dto";
import DefaultPageLayout from "@/layouts/DefaultPageLayout.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useClipboard } from "@/services/clipboard";
import { detectUserOS } from "@/services/utils";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";
import ProjectsListViewCard from "@/views/projects/ProjectsListViewCard.vue";

const api = useApi();
const route = useRoute();
const router = useRouter();
const modals = useModalsStore();
const toasts = useToastsStore();
const clipboard = useClipboard();
const isLoading = ref(true);
const projects = ref<ProjectDto[]>([]);
const isWindows = detectUserOS().toLowerCase().includes("win");
const selectedShell = ref(isWindows ? "powershell" : "bash");
const cards = useTemplateRef<HTMLDivElement>("cards");
const cardFirstSectionWidth = ref(-1);

const tenantName = computed(() => route.params.tenantName as string);
const bashSnippet = computed(
  () => `git clone git@github.com:probabl-ai/skore-demo-project.git
cd skore-demo-project
python3 -m venv venv
source venv/bin/activate
pip install -r requirements.txt
skore-hub-login
python demo.py --tenant="${tenantName.value}" --name="demo"`
);
const powershellSnippet = computed(
  () => `git clone https://github.com/probabl-ai/skore-demo-project.git
cd .\\skore-demo-project\\
python -m venv venv
.\\venv\\Scripts\\Activate.ps1
pip install -r requirements.txt
skore-hub-login
python demo.py --tenant="${tenantName.value}" --name="demo"`
);
const snippet = computed(() =>
  selectedShell.value === "powershell" ? powershellSnippet.value : bashSnippet.value
);

async function refreshProjects() {
  isLoading.value = true;
  const results = await api.listProjects(tenantName.value);
  if (results !== null) {
    projects.value = results;
  } else {
    router.replace({ name: RouteNames.NotFound });
  }
  isLoading.value = false;
}

async function copySnippet() {
  await clipboard.copyText(snippet.value);
}

async function deleteProject(projectName: string) {
  if (
    await modals.confirm({
      title: "Delete project",
      message: `Do you really want to delete project ${projectName}?`,
    })
  ) {
    const success = await api.deleteProject(tenantName.value, projectName);
    if (success) {
      toasts.addToast(`Project ${projectName} deleted`, "success", {
        duration: 5,
      });
    }
  }
  await refreshProjects();
}

onMounted(async () => {
  await refreshProjects();
});
</script>

<template>
  <DefaultPageLayout :breadcrumb-items="[{ label: tenantName }]">
    <div class="projects-view">
      <div v-if="isLoading" class="loading">
        <SkoreSpinner />
      </div>
      <div v-else class="wrapper">
        <header>
          <h6>Projects</h6>
          <div class="subtitle paragraph-s">Browse the projects you created here.</div>
        </header>
        <div v-if="projects.length > 0" class="projects" ref="cards">
          <ProjectsListViewCard
            v-for="project in projects"
            :key="project.id"
            :project="project"
            :tenant-name="tenantName"
            :first-section-width="cardFirstSectionWidth"
            @delete="deleteProject(project.name)"
          />
        </div>
        <div v-else class="placeholder">
          <div class="icon">
            <i class="icon icon-scan-cube" />
          </div>
          <h6>No projects yet</h6>
          <div class="subtitle paragraph-s">
            Projects appear here when you create them using the skore library in your code.
            <br />
            Check the <a href="https://docs.skore.probabl.ai/">documentation</a> to get started or
            try our tutorial:
          </div>
          <div class="snippet">
            <div class="help">
              <div>
                Run this in a
                <span class="shell-dropdown">
                  <DropdownButton :label="selectedShell" icon-right="icon-chevron-down">
                    <DropdownButtonItem label="bash" @click="selectedShell = 'bash'" />
                    <DropdownButtonItem label="powershell" @click="selectedShell = 'powershell'" />
                  </DropdownButton>
                </span>
                terminal.
              </div>
              <SimpleButton icon-left="icon-copy" kind="tertiary" @click="copySnippet" />
            </div>
            <div class="lines">
              <div v-for="(line, i) in snippet.split('\n')" :key="i" class="line">
                <span class="prompt">$ </span>{{ line }}
              </div>
            </div>
          </div>
          <div class="help paragraph-s">
            <em>
              You'll need to install <a href="https://git-scm.com/downloads">git</a> and
              <a href="https://www.python.org/downloads/">Python</a> (version 3.10, to 3.13).
            </em>
          </div>
        </div>
      </div>
    </div>
  </DefaultPageLayout>
</template>

<style scoped>
.projects-view {
  .loading {
    margin-top: 45dvh;
    text-align: center;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    padding: calc(var(--spacing-32) - var(--spacing-12)) 0 var(--spacing-32) 0;
    gap: var(--spacing-32);

    h6 {
      i {
        margin-right: var(--spacing-6);
        color: var(--color-icon-tertiary);
      }
    }

    .subtitle {
      color: var(--color-text-secondary);
    }

    .projects {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12);
    }

    .placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: var(--spacing-80);
      gap: var(--spacing-12);
      text-align: center;

      .icon {
        margin-bottom: var(--spacing-12);

        i {
          padding: var(--spacing-8);
          border-radius: var(--radius-s);
          background-color: var(--color-button-background-tertiary-hover);
          font-size: var(--font-size-lg);
          line-height: calc(var(--font-size-sm) * 1.2);
          vertical-align: middle;
        }
      }

      .snippet {
        padding: var(--spacing-6);
        border-radius: var(--radius-s);
        background-color: var(--color-background-secondary);
        text-align: left;

        .help {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 0 var(--spacing-6);

          .shell-dropdown {
            :deep(.button) {
              background-color: var(--color-background-primary);
            }
          }
        }

        .lines {
          padding: var(--spacing-6);
          border-radius: var(--radius-m);
          margin: var(--spacing-6);
          background-color: var(--color-background-primary);
          box-shadow: var(--box-shadow-m);
          color: var(--color-text-branding);
          font-family: GeistMono, monospace;

          .prompt {
            color: #009200;
            user-select: none;
          }
        }
      }
    }
  }
}
</style>
